import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  FaArrowLeft,
  FaShieldAlt,
  FaTruck,
  FaHospital,
  FaBoxes,
  FaChartLine,
  FaHandshake,
  FaClock,
  FaFileAlt,
  FaUsers,
  FaMapMarkerAlt,
  FaCheck,
  FaNetworkWired,
  FaAward,
  FaPlus,
  FaStore,
  FaCapsules
} from 'react-icons/fa';

/* ============================================================
   IMAGE IMPORTS
   ============================================================ */

import ashrafiaLogo from '../logos/Ashrafiadistributor/AshrafiaDistributorlogo.png';
import pharmacyImage from '../logos/Ashrafiadistributor/AshrafiaDistributor1.png';
import distributorVan from '../logos/Ashrafiadistributor/AshrafiaDistributor2.png';


/* ============================================================
   COMPONENT
   ============================================================ */

export default function AshrafiaDistributor() {

  const navigate = useNavigate();


  /* ==========================================================
     SCROLL REVEAL ANIMATION
     ========================================================== */

  useEffect(() => {

    const elements = document.querySelectorAll('.reveal');

    const observer = new IntersectionObserver(
      (entries) => {

        entries.forEach((entry) => {

          if (entry.isIntersecting) {

            entry.target.classList.add('active');

          }

        });

      },
      {
        threshold: 0.12
      }
    );


    elements.forEach((element) => {

      observer.observe(element);

    });


    return () => {

      elements.forEach((element) => {

        observer.unobserve(element);

      });

    };

  }, []);


  return (

    <>

      {/* ======================================================
          GLOBAL CSS
          ====================================================== */}

      <style>{`

        * {
          box-sizing: border-box;
        }


        html {
          scroll-behavior: smooth;
          width: 100%;
          overflow-x: hidden;
        }


        body {
          margin: 0;
          padding: 0;
          width: 100%;
          overflow-x: hidden;
        }


        #root {
          width: 100%;
          min-height: 100vh;
          margin: 0;
          padding: 0;
        }


        /* ====================================================
           MAIN PAGE
           ==================================================== */

        .ashrafia-page {

          width: 100%;
          min-height: 100vh;

          background:

            radial-gradient(
              circle at top left,
              rgba(21, 128, 61, 0.12),
              transparent 35%
            ),

            linear-gradient(
              135deg,
              #07140d 0%,
              #0f172a 50%,
              #07140d 100%
            );

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          color: #263238;

          overflow-x: hidden;
        }


        /* ====================================================
           SCROLL REVEAL
           ==================================================== */

        .reveal {

          opacity: 0;

          transform:
            translateY(35px);

          transition:
            opacity 0.8s ease,
            transform 0.8s ease;
        }


        .reveal.active {

          opacity: 1;

          transform:
            translateY(0);
        }


        .reveal-delay-1 {
          transition-delay: 0.1s;
        }


        .reveal-delay-2 {
          transition-delay: 0.2s;
        }


        .reveal-delay-3 {
          transition-delay: 0.3s;
        }


        /* ====================================================
           PAGE WRAPPER
           FULL WIDTH
           ==================================================== */

        .page-wrapper {

          width: 100%;

          margin: 0;

          padding:
            24px 0 50px;

        }


        /* ====================================================
           TOP NAV
           ==================================================== */

        .top-nav {

          width: 100%;

          margin:
            0 0 18px;

          padding:
            0 24px;

        }


        .back-button {

          display: inline-flex;

          align-items: center;

          justify-content: center;

          gap: 8px;

          padding:
            11px 20px;

          border: none;

          border-radius: 7px;

          background:
            linear-gradient(
              135deg,
              #15803d,
              #166534
            );

          color: #fff;

          font-size: 13px;

          font-weight: 700;

          cursor: pointer;

          box-shadow:
            0 7px 18px
            rgba(21, 128, 61, 0.25);

          transition:
            transform 0.3s ease,
            box-shadow 0.3s ease,
            background 0.3s ease;
        }


        .back-button:hover {

          transform:
            translateY(-3px);

          box-shadow:
            0 12px 25px
            rgba(21, 128, 61, 0.35);

          background:
            linear-gradient(
              135deg,
              #16a34a,
              #15803d
            );
        }


        .back-button:active {

          transform:
            translateY(0);
        }


        /* ====================================================
           MAIN CARD
           FULL BROWSER WIDTH
           ==================================================== */

        .poster-card {

          width: 100%;

          max-width: none;

          margin: 0;

          background: #f8faf8;

          border-radius: 0;

          overflow: hidden;

          box-shadow:
            0 25px 70px
            rgba(0, 0, 0, 0.5);

          border:
            1px solid
            rgba(255, 255, 255, 0.1);
        }


        /* ====================================================
           TOP BANNER
           ==================================================== */

        .top-banner {

          width: 100%;

          min-height: 52px;

          padding:
            0 40px;

          background:
            linear-gradient(
              90deg,
              #166534,
              #15803d,
              #166534
            );

          color: #fff;

          display: flex;

          align-items: center;

          justify-content: space-between;
        }


        .top-banner-text {

          font-size: 14px;

          font-weight: 800;

          letter-spacing: 2px;
        }


        .top-banner-right {

          display: flex;

          align-items: center;

          gap: 10px;
        }


        .top-banner-icon {

          width: 29px;

          height: 29px;

          border-radius: 50%;

          background: #fff;

          color: #15803d;

          display: flex;

          align-items: center;

          justify-content: center;

          font-size: 14px;
        }


        /* ====================================================
           HERO
           ==================================================== */

        .hero-section {

          width: 100%;

          display: grid;

          grid-template-columns:
            minmax(360px, 0.82fr)
            minmax(0, 1.55fr);

          min-height: 470px;

          background: #fff;
        }


        .hero-left {

          position: relative;

          padding:
            50px 60px;

          display: flex;

          flex-direction: column;

          justify-content: center;

          background:

            radial-gradient(
              circle at 10% 10%,
              rgba(21, 128, 61, 0.07),
              transparent 35%
            ),

            #ffffff;

          overflow: hidden;
        }


        .hero-left::after {

          content: '';

          position: absolute;

          width: 330px;

          height: 330px;

          border:
            1px solid
            rgba(21, 128, 61, 0.12);

          border-radius: 50%;

          right: -220px;

          top: 40px;

          pointer-events: none;
        }


        .logo-wrapper {

          margin-bottom: 24px;

          animation:
            fadeDown 0.9s ease forwards;
        }


        .logo-image {

          display: block;

          width:
            min(300px, 100%);

          height: auto;

          object-fit: contain;

          object-position:
            left center;
        }


        .hero-title {

          margin: 0;

          color: #15803d;

          font-size:
            clamp(42px, 5vw, 70px);

          line-height: 0.95;

          font-weight: 900;

          letter-spacing: -1px;

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          animation:
            slideLeft
            0.9s ease
            0.1s both;
        }


        .hero-title-dark {

          color: #1f2937;
        }


        .hero-line {

          width: 100px;

          height: 4px;

          margin:
            22px 0 18px;

          background: #15803d;

          border-radius: 50px;

          animation:
            expandLine
            1s ease
            0.4s both;
        }


        .hero-tagline {

          margin:
            0 0 14px;

          color: #166534;

          font-size:
            clamp(17px, 2vw, 23px);

          line-height: 1.35;

          font-weight: 700;

          animation:
            fadeUp
            0.9s ease
            0.3s both;
        }


        .hero-description {

          max-width: 500px;

          margin: 0;

          color: #475569;

          font-size: 15px;

          line-height: 1.75;

          animation:
            fadeUp
            0.9s ease
            0.4s both;
        }


        /* ====================================================
           HERO IMAGE
           ==================================================== */

        .hero-right {

          position: relative;

          min-height: 470px;

          overflow: hidden;

          background: #e2e8f0;
        }


        .hero-image {

          width: 100%;

          height: 100%;

          min-height: 470px;

          display: block;

          object-fit: cover;

          transition:
            transform 1.2s ease;
        }


        .hero-right:hover
        .hero-image {

          transform:
            scale(1.04);
        }


        .hero-overlay {

          position: absolute;

          inset: 0;

          background:
            linear-gradient(
              90deg,
              rgba(255,255,255,0.08),
              transparent 40%,
              rgba(0,0,0,0.12)
            );

          pointer-events: none;
        }


        .hero-division-badge {

          position: absolute;

          top: 24px;

          right: 24px;

          max-width: 360px;

          padding:
            14px 24px;

          border-radius: 14px;

          background:
            rgba(21, 128, 61, 0.94);

          color: #fff;

          font-size: 17px;

          font-weight: 800;

          letter-spacing: 1px;

          text-align: center;

          box-shadow:
            0 12px 28px
            rgba(0,0,0,0.2);

          backdrop-filter:
            blur(6px);

          animation:
            slideRight
            0.9s ease
            0.2s both;
        }


        .trusted-badge {

          position: absolute;

          left: 28px;

          bottom: 28px;

          width: 190px;

          min-height: 150px;

          border-radius: 50%;

          background:
            rgba(255,255,255,0.95);

          border:
            3px solid
            #15803d;

          display: flex;

          flex-direction: column;

          align-items: center;

          justify-content: center;

          text-align: center;

          padding: 20px;

          box-shadow:
            0 12px 30px
            rgba(0,0,0,0.22);

          animation:
            floatBadge 4s
            ease-in-out infinite,
            fadeUp 0.9s
            ease 0.5s both;
        }


        .trusted-icon {

          color: #15803d;

          font-size: 31px;

          margin-bottom: 10px;
        }


        .trusted-text {

          color: #166534;

          font-size: 12px;

          line-height: 1.35;

          font-weight: 800;
        }


        /* ====================================================
           SECTION
           ==================================================== */

        .section {

          width: 100%;

          padding:
            60px
            clamp(30px, 5vw, 90px);

          background: #f8faf8;
        }


        .section-white {

          background: #fff;
        }


        /* ====================================================
           SECTION HEADING
           ==================================================== */

        .section-heading {

          display: flex;

          align-items: center;

          gap: 18px;

          margin-bottom: 30px;
        }


        .section-heading h2 {

          margin: 0;

          color: #166534;

          font-size: 22px;

          font-weight: 900;

          letter-spacing: 1.2px;

          white-space: nowrap;
        }


        .section-heading-line {

          flex: 1;

          height: 2px;

          background: #bbf7d0;

          position: relative;
        }


        .section-heading-line::after {

          content: '';

          position: absolute;

          right: 0;

          top: 50%;

          width: 8px;

          height: 8px;

          transform:
            translateY(-50%);

          background: #15803d;

          border-radius: 50%;
        }


        /* ====================================================
           TWO COLUMN
           ==================================================== */

        .two-column {

          width: 100%;

          display: grid;

          grid-template-columns:
            minmax(0, 0.95fr)
            minmax(0, 1.05fr);

          gap: 40px;
        }


        .about-card,
        .services-card {

          position: relative;

          padding: 34px;

          border-radius: 14px;

          background: #fff;

          border:
            1px solid
            #dce8df;

          box-shadow:
            0 8px 25px
            rgba(15, 23, 42, 0.05);

          transition:
            transform 0.35s ease,
            box-shadow 0.35s ease;
        }


        .about-card:hover,
        .services-card:hover {

          transform:
            translateY(-6px);

          box-shadow:
            0 18px 35px
            rgba(15, 23, 42, 0.10);
        }


        .about-card {

          padding-left: 90px;
        }


        .about-text {

          margin:
            0 0 20px;

          color: #475569;

          font-size: 14px;

          line-height: 1.8;
        }


        .about-icons {

          position: absolute;

          left: 22px;

          top: 38px;

          bottom: 38px;

          width: 45px;

          display: flex;

          flex-direction: column;

          justify-content:
            space-between;

          align-items: center;
        }


        .about-icons::before {

          content: '';

          position: absolute;

          top: 20px;

          bottom: 20px;

          left: 50%;

          width: 2px;

          transform:
            translateX(-50%);

          background: #bbf7d0;

          z-index: 0;
        }


        .about-icon {

          position: relative;

          z-index: 1;

          width: 43px;

          height: 43px;

          border-radius: 50%;

          display: flex;

          align-items: center;

          justify-content: center;

          background: #fff;

          border:
            2px solid
            #15803d;

          color: #15803d;

          font-size: 17px;

          transition:
            transform 0.3s ease,
            background 0.3s ease,
            color 0.3s ease;
        }


        .about-icon:hover {

          transform:
            scale(1.15);

          background:
            #15803d;

          color: #fff;
        }


        /* ====================================================
           SERVICES
           ==================================================== */

        .services-list {

          display: flex;

          flex-direction: column;

          gap: 15px;
        }


        .service-item {

          display: flex;

          align-items: flex-start;

          gap: 17px;

          padding: 17px;

          border-radius: 10px;

          background: #f8faf8;

          border:
            1px solid
            #e2e8e2;

          transition:
            transform 0.3s ease,
            background 0.3s ease,
            border-color 0.3s ease,
            box-shadow 0.3s ease;
        }


        .service-item:hover {

          transform:
            translateX(7px);

          background:
            #f0fdf4;

          border-color:
            #86efac;

          box-shadow:
            0 8px 20px
            rgba(21,128,61,0.08);
        }


        .service-icon {

          width: 45px;

          height: 45px;

          flex-shrink: 0;

          border-radius: 50%;

          background: #fff;

          border:
            2px solid
            #86efac;

          color: #15803d;

          display: flex;

          align-items: center;

          justify-content: center;

          font-size: 18px;

          transition:
            transform 0.3s ease,
            background 0.3s ease,
            color 0.3s ease;
        }


        .service-item:hover
        .service-icon {

          transform:
            rotate(8deg)
            scale(1.1);

          background:
            #15803d;

          color: #fff;
        }


        .service-content h3 {

          margin:
            0 0 5px;

          color:
            #1f2937;

          font-size: 15px;

          font-weight: 800;
        }


        .service-content p {

          margin: 0;

          color:
            #64748b;

          font-size: 13px;

          line-height: 1.5;
        }


        /* ====================================================
           STRENGTHS
           ==================================================== */

        .strengths-coverage {

          width: 100%;

          display: grid;

          grid-template-columns:
            minmax(0, 1fr)
            minmax(0, 1fr);

          gap: 40px;
        }


        .strength-card,
        .coverage-card {

          padding: 34px;

          border-radius: 14px;

          background: #fff;

          border:
            1px solid
            #dce8df;

          box-shadow:
            0 8px 25px
            rgba(15, 23, 42, 0.05);
        }


        .strength-grid {

          display: grid;

          grid-template-columns:
            repeat(5, minmax(0, 1fr));

          gap: 12px;
        }


        .strength-item {

          min-height: 130px;

          padding:
            17px 8px;

          border-radius: 10px;

          background: #f8faf8;

          border:
            1px solid
            #e2e8e2;

          display: flex;

          flex-direction: column;

          justify-content: center;

          align-items: center;

          text-align: center;

          transition:
            transform 0.3s ease,
            border-color 0.3s ease,
            box-shadow 0.3s ease;
        }


        .strength-item:hover {

          transform:
            translateY(-7px);

          border-color:
            #86efac;

          box-shadow:
            0 12px 24px
            rgba(21,128,61,0.10);
        }


        .strength-icon {

          width: 45px;

          height: 45px;

          border-radius: 50%;

          display: flex;

          align-items: center;

          justify-content: center;

          color: #15803d;

          border:
            2px solid
            #86efac;

          background: #fff;

          font-size: 18px;

          margin-bottom: 10px;

          transition:
            transform 0.3s ease,
            background 0.3s ease,
            color 0.3s ease;
        }


        .strength-item:hover
        .strength-icon {

          transform:
            scale(1.1)
            rotate(5deg);

          background:
            #15803d;

          color: #fff;
        }


        .strength-text {

          color:
            #334155;

          font-size: 11px;

          font-weight: 700;

          line-height: 1.35;
        }


        /* ====================================================
           COVERAGE
           ==================================================== */

        .coverage-content {

          display: grid;

          grid-template-columns:
            minmax(0, 1.3fr)
            minmax(180px, 0.7fr);

          gap: 18px;
        }


        .presence-box,
        .expansion-box {

          border-radius: 12px;

          background:
            #f8faf8;

          border:
            1px solid
            #dce8df;

          padding: 20px;
        }


        .coverage-label {

          display: inline-flex;

          align-items: center;

          justify-content: center;

          min-height: 30px;

          width: 100%;

          border-radius: 20px;

          background:
            #15803d;

          color: #fff;

          font-size: 11px;

          font-weight: 800;

          letter-spacing: 0.8px;

          margin-bottom: 17px;
        }


        .cities {

          display: flex;

          gap: 10px;

          flex-wrap: wrap;

          margin-bottom: 18px;
        }


        .city {

          display: flex;

          align-items: center;

          gap: 6px;

          padding:
            8px 12px;

          border-radius: 7px;

          background: #fff;

          border:
            1px solid
            #dce8df;

          color:
            #334155;

          font-size: 12px;

          font-weight: 700;

          transition:
            transform 0.3s ease,
            border-color 0.3s ease;
        }


        .city:hover {

          transform:
            translateY(-3px);

          border-color:
            #86efac;
        }


        .city svg {

          color:
            #15803d;
        }


        .map-box {

          min-height: 140px;

          border-radius: 10px;

          background:

            radial-gradient(
              circle at 50% 50%,
              rgba(21,128,61,0.12),
              transparent 60%
            ),

            #ecfdf5;

          border:
            1px dashed
            #86efac;

          display: flex;

          align-items: center;

          justify-content: center;

          position: relative;

          overflow: hidden;
        }


        .map-box::before {

          content: '';

          width: 150px;

          height: 100px;

          border:
            2px solid
            rgba(21,128,61,0.28);

          border-radius:
            48% 52% 42% 58%;

          transform:
            rotate(-8deg);

          position: absolute;
        }


        .map-box::after {

          content: '';

          width: 90px;

          height: 60px;

          border:
            2px solid
            rgba(21,128,61,0.2);

          border-radius: 50%;

          position: absolute;

          transform:
            rotate(30deg);
        }


        .map-text {

          position: relative;

          z-index: 2;

          color:
            #15803d;

          font-size: 12px;

          font-weight: 800;

          text-align: center;
        }


        .expansion-box {

          display: flex;

          flex-direction: column;

          align-items: center;
        }


        .expansion-content {

          flex: 1;

          display: flex;

          flex-direction: column;

          align-items: center;

          justify-content: center;

          text-align: center;
        }


        .expansion-icon {

          width: 75px;

          height: 75px;

          border-radius: 50%;

          background: #fff;

          border:
            3px solid
            #86efac;

          color:
            #15803d;

          display: flex;

          align-items: center;

          justify-content: center;

          font-size: 28px;

          margin-bottom: 18px;

          animation:
            pulseGreen 2.5s infinite;
        }


        .expansion-text {

          max-width: 180px;

          margin: 0;

          color:
            #475569;

          font-size: 13px;

          font-weight: 700;

          line-height: 1.5;
        }


        /* ====================================================
           BOTTOM
           ==================================================== */

        .bottom-section {

          width: 100%;

          display: grid;

          grid-template-columns:
            minmax(300px, 1fr)
            minmax(0, 1.35fr);

          gap: 40px;

          align-items:
            stretch;
        }


        .van-wrapper {

          min-height: 300px;

          border-radius: 14px;

          overflow: hidden;

          position: relative;

          box-shadow:
            0 10px 30px
            rgba(15,23,42,0.12);
        }


        .van-image {

          width: 100%;

          height: 100%;

          min-height: 300px;

          display: block;

          object-fit: cover;

          transition:
            transform 0.9s ease;
        }


        .van-wrapper:hover
        .van-image {

          transform:
            scale(1.06);
        }


        .van-overlay {

          position: absolute;

          inset:
            auto 0 0;

          padding: 25px;

          background:
            linear-gradient(
              transparent,
              rgba(0,0,0,0.75)
            );
        }


        .van-overlay-text {

          margin: 0;

          color: #fff;

          font-size: 19px;

          font-weight: 800;
        }


        .why-card {

          padding: 34px;

          border-radius: 14px;

          background: #fff;

          border:
            1px solid
            #dce8df;

          box-shadow:
            0 8px 25px
            rgba(15, 23, 42, 0.05);
        }


        .why-grid {

          display: grid;

          grid-template-columns:
            repeat(2, minmax(0, 1fr));

          gap: 15px;
        }


        .why-item {

          display: flex;

          align-items: flex-start;

          gap: 14px;

          padding: 19px;

          border-radius: 10px;

          background:
            #f8faf8;

          border:
            1px solid
            #e2e8e2;

          transition:
            transform 0.3s ease,
            box-shadow 0.3s ease,
            border-color 0.3s ease;
        }


        .why-item:hover {

          transform:
            translateY(-5px);

          border-color:
            #86efac;

          box-shadow:
            0 12px 25px
            rgba(21,128,61,0.08);
        }


        .why-icon {

          width: 42px;

          height: 42px;

          flex-shrink: 0;

          border-radius: 50%;

          background:
            #15803d;

          color: #fff;

          display: flex;

          align-items: center;

          justify-content: center;

          transition:
            transform 0.3s ease;
        }


        .why-item:hover
        .why-icon {

          transform:
            rotate(10deg)
            scale(1.1);
        }


        .why-text {

          color:
            #334155;

          font-size: 13px;

          line-height: 1.45;

          font-weight: 700;
        }


        /* ====================================================
           FOOTER
           ==================================================== */

        .footer {

          width: 100%;

          min-height: 100px;

          padding:
            20px
            clamp(25px, 5vw, 80px);

          display: grid;

          grid-template-columns:
            110px
            1fr
            auto;

          gap: 25px;

          align-items: center;

          background: #fff;

          border-top:
            3px solid
            #15803d;
        }


        .footer-number {

          min-height: 70px;

          display: flex;

          align-items: center;

          justify-content: center;

          background:
            #15803d;

          color: #fff;

          font-size: 42px;

          font-weight: 900;

          clip-path:
            polygon(
              0 0,
              100% 0,
              82% 100%,
              0 100%
            );
        }


        .footer-company {

          display: flex;

          flex-direction: column;

          gap: 4px;
        }


        .footer-company-name {

          color:
            #15803d;

          font-size: 18px;

          font-weight: 900;

          letter-spacing: 0.5px;
        }


        .footer-tagline {

          color:
            #64748b;

          font-size: 12px;

          font-style: italic;
        }


        .footer-logo {

          width: 230px;

          max-width: 100%;

          height: auto;

          object-fit: contain;
        }


        /* ====================================================
           KEYFRAMES
           ==================================================== */

        @keyframes fadeDown {

          from {

            opacity: 0;

            transform:
              translateY(-25px);
          }

          to {

            opacity: 1;

            transform:
              translateY(0);
          }
        }


        @keyframes fadeUp {

          from {

            opacity: 0;

            transform:
              translateY(25px);
          }

          to {

            opacity: 1;

            transform:
              translateY(0);
          }
        }


        @keyframes slideLeft {

          from {

            opacity: 0;

            transform:
              translateX(-35px);
          }

          to {

            opacity: 1;

            transform:
              translateX(0);
          }
        }


        @keyframes slideRight {

          from {

            opacity: 0;

            transform:
              translateX(35px);
          }

          to {

            opacity: 1;

            transform:
              translateX(0);
          }
        }


        @keyframes expandLine {

          from {

            width: 0;

            opacity: 0;
          }

          to {

            width: 100px;

            opacity: 1;
          }
        }


        @keyframes floatBadge {

          0%,
          100% {

            transform:
              translateY(0);
          }

          50% {

            transform:
              translateY(-8px);
          }
        }


        @keyframes pulseGreen {

          0% {

            box-shadow:
              0 0 0 0
              rgba(21,128,61,0.25);
          }

          70% {

            box-shadow:
              0 0 0 13px
              rgba(21,128,61,0);
          }

          100% {

            box-shadow:
              0 0 0 0
              rgba(21,128,61,0);
          }
        }


        /* ====================================================
           LARGE SCREEN
           ==================================================== */

        @media (min-width: 1600px) {

          .hero-left {

            padding:
              70px
              90px;
          }


          .section {

            padding-left:
              100px;

            padding-right:
              100px;
          }


          .top-banner {

            padding-left:
              80px;

            padding-right:
              80px;
          }


          .hero-section {

            min-height:
              540px;
          }


          .hero-right {

            min-height:
              540px;
          }


          .hero-image {

            min-height:
              540px;
          }

        }


        /* ====================================================
           TABLET / LAPTOP
           ==================================================== */

        @media (max-width: 1100px) {

          .page-wrapper {

            padding:
              20px 0 40px;
          }


          .top-nav {

            padding:
              0 18px;
          }


          .hero-section {

            grid-template-columns:
              minmax(320px, 1fr)
              minmax(0, 1.25fr);
          }


          .hero-left {

            padding:
              40px 35px;
          }


          .section {

            padding:
              45px 35px;
          }


          .two-column,
          .strengths-coverage,
          .bottom-section {

            gap:
              25px;
          }


          .strength-grid {

            grid-template-columns:
              repeat(3, 1fr);
          }


          .footer {

            grid-template-columns:
              90px
              1fr
              180px;
          }

        }


        /* ====================================================
           TABLET
           ==================================================== */

        @media (max-width: 850px) {

          .hero-section {

            grid-template-columns:
              1fr;
          }


          .hero-left {

            min-height: auto;

            padding:
              45px 40px;
          }


          .hero-right {

            min-height:
              400px;
          }


          .hero-image {

            min-height:
              400px;
          }


          .two-column,
          .strengths-coverage,
          .bottom-section {

            grid-template-columns:
              1fr;
          }


          .about-card {

            padding-left:
              95px;
          }


          .strength-grid {

            grid-template-columns:
              repeat(5, 1fr);
          }


          .bottom-section {

            gap:
              25px;
          }


          .van-wrapper {

            min-height:
              280px;
          }


          .van-image {

            min-height:
              280px;
          }

        }


        /* ====================================================
           MOBILE
           ==================================================== */

        @media (max-width: 600px) {

          .ashrafia-page {

            background:
              #f8faf8;
          }


          .page-wrapper {

            padding:
              0;
          }


          .top-nav {

            padding:
              12px;

            margin:
              0;

            background:
              #0f172a;
          }


          .back-button {

            width:
              100%;

            padding:
              12px;
          }


          .poster-card {

            border-radius:
              0;

            border:
              none;

            box-shadow:
              none;
          }


          .top-banner {

            min-height:
              50px;

            padding:
              8px 14px;
          }


          .top-banner-text {

            font-size:
              10px;

            letter-spacing:
              1px;

            line-height:
              1.3;
          }


          .top-banner-icon {

            width:
              26px;

            height:
              26px;

            flex-shrink:
              0;
          }


          .hero-section {

            display:
              flex;

            flex-direction:
              column;
          }


          .hero-left {

            width:
              100%;

            padding:
              35px 22px
              30px;
          }


          .logo-image {

            width:
              220px;

            max-width:
              80%;
          }


          .hero-title {

            font-size:
              48px;
          }


          .hero-line {

            margin:
              16px 0;
          }


          .hero-tagline {

            font-size:
              17px;
          }


          .hero-description {

            font-size:
              13px;

            line-height:
              1.65;
          }


          .hero-right {

            width:
              100%;

            min-height:
              310px;
          }


          .hero-image {

            min-height:
              310px;
          }


          .hero-division-badge {

            top:
              14px;

            right:
              14px;

            left:
              14px;

            max-width:
              none;

            padding:
              10px 14px;

            font-size:
              11px;

            letter-spacing:
              0.7px;
          }


          .trusted-badge {

            left:
              15px;

            bottom:
              15px;

            width:
              125px;

            min-height:
              125px;

            padding:
              14px;

            border-width:
              2px;
          }


          .trusted-icon {

            font-size:
              23px;

            margin-bottom:
              6px;
          }


          .trusted-text {

            font-size:
              8px;
          }


          .section {

            padding:
              35px 16px;
          }


          .section-heading {

            gap:
              10px;

            margin-bottom:
              22px;
          }


          .section-heading h2 {

            font-size:
              15px;

            letter-spacing:
              0.7px;

            white-space:
              normal;
          }


          .section-heading-line {

            min-width:
              20px;
          }


          .two-column,
          .strengths-coverage,
          .bottom-section {

            grid-template-columns:
              1fr;
          }


          .about-card,
          .services-card,
          .strength-card,
          .coverage-card,
          .why-card {

            padding:
              22px;
          }


          .about-card {

            padding-left:
              22px;

            padding-top:
              82px;
          }


          .about-icons {

            top:
              20px;

            left:
              22px;

            right:
              22px;

            bottom:
              auto;

            width:
              auto;

            height:
              45px;

            flex-direction:
              row;
          }


          .about-icons::before {

            top:
              50%;

            bottom:
              auto;

            left:
              20px;

            right:
              20px;

            width:
              auto;

            height:
              2px;

            transform:
              translateY(-50%);
          }


          .about-text {

            font-size:
              13px;

            line-height:
              1.7;
          }


          .about-icon {

            width:
              40px;

            height:
              40px;

            font-size:
              15px;
          }


          .service-item {

            padding:
              14px;

            gap:
              12px;
          }


          .service-icon {

            width:
              39px;

            height:
              39px;

            font-size:
              15px;
          }


          .service-content h3 {

            font-size:
              13px;
          }


          .service-content p {

            font-size:
              11px;
          }


          .strength-grid {

            grid-template-columns:
              repeat(2, 1fr);
          }


          .strength-item {

            min-height:
              115px;
          }


          .coverage-content {

            grid-template-columns:
              1fr;
          }


          .map-box {

            min-height:
              120px;
          }


          .bottom-section {

            gap:
              20px;
          }


          .van-wrapper {

            min-height:
              230px;
          }


          .van-image {

            min-height:
              230px;
          }


          .why-grid {

            grid-template-columns:
              1fr;
          }


          .why-item {

            padding:
              15px;
          }


          .why-icon {

            width:
              38px;

            height:
              38px;
          }


          .why-text {

            font-size:
              12px;
          }


          .footer {

            padding:
              18px;

            grid-template-columns:
              55px
              1fr;

            gap:
              15px;
          }


          .footer-number {

            min-height:
              55px;

            font-size:
              28px;
          }


          .footer-company-name {

            font-size:
              13px;
          }


          .footer-tagline {

            font-size:
              10px;
          }


          .footer-logo {

            grid-column:
              1 / -1;

            width:
              180px;

            margin:
              5px auto 0;
          }

        }


        /* ====================================================
           VERY SMALL MOBILE
           ==================================================== */

        @media (max-width: 380px) {

          .hero-title {

            font-size:
              41px;
          }


          .hero-left {

            padding:
              30px 18px;
          }


          .section {

            padding:
              30px 12px;
          }


          .strength-grid {

            gap:
              8px;
          }


          .strength-item {

            padding:
              12px 5px;
          }


          .strength-icon {

            width:
              39px;

            height:
              39px;
          }


          .strength-text {

            font-size:
              10px;
          }


          .footer {

            grid-template-columns:
              48px
              1fr;
          }


          .footer-number {

            min-height:
              48px;

            font-size:
              24px;
          }

        }


        /* ====================================================
           REDUCED MOTION
           ==================================================== */

        @media (prefers-reduced-motion: reduce) {

          *,
          *::before,
          *::after {

            animation-duration:
              0.01ms !important;

            animation-iteration-count:
              1 !important;

            transition-duration:
              0.01ms !important;

            scroll-behavior:
              auto !important;
          }


          .reveal {

            opacity:
              1;

            transform:
              none;
          }

        }

      `}</style>


      {/* ======================================================
          PAGE
          ====================================================== */}

      <div className="ashrafia-page">

        <div className="page-wrapper">


          {/* ==================================================
              BACK BUTTON
              ================================================== */}

          <div className="top-nav">

            <button
              className="back-button"
              onClick={() => navigate('/')}
            >

              <FaArrowLeft />

              Back to All Sectors

            </button>

          </div>


          {/* ==================================================
              MAIN CARD
              ================================================== */}

          <main className="poster-card">


            {/* =================================================
                TOP BANNER
                ================================================= */}

            <div className="top-banner">

              <span className="top-banner-text">

                PHARMACEUTICAL DISTRIBUTION DIVISION

              </span>


              <div className="top-banner-right">

                <div className="top-banner-icon">

                  <FaPlus />

                </div>

              </div>

            </div>


            {/* =================================================
                HERO
                ================================================= */}

            <section className="hero-section">


              {/* LEFT */}

              <div className="hero-left">


                <div className="logo-wrapper">

                  <img
                    src={ashrafiaLogo}
                    alt="Ashrafia Distributor Logo"
                    className="logo-image"
                  />

                </div>


                <h1 className="hero-title">

                  PHARMA

                  <br />

                  <span className="hero-title-dark">

                    DIVISION

                  </span>

                </h1>


                <div className="hero-line" />


                <p className="hero-tagline">

                  Delivering Health.

                  <br />

                  Ensuring Trust.

                </p>


                <p className="hero-description">

                  A commitment to quality healthcare products
                  and dependable distribution across Gujranwala
                  and Sialkot.

                </p>

              </div>


              {/* RIGHT */}

              <div className="hero-right">

                <img
                  src={pharmacyImage}
                  alt="Ashrafia Pharmaceutical Distribution"
                  className="hero-image"
                />


                <div className="hero-overlay" />


                <div className="hero-division-badge">

                  PHARMACEUTICAL

                  <br />

                  DISTRIBUTION DIVISION

                </div>


                <div className="trusted-badge">

                  <FaHandshake
                    className="trusted-icon"
                  />

                  <span className="trusted-text">

                    YOUR TRUSTED

                    <br />

                    PARTNER IN

                    <br />

                    HEALTHCARE

                    <br />

                    DISTRIBUTION

                  </span>

                </div>

              </div>

            </section>


            {/* =================================================
                ABOUT + SERVICES
                ================================================= */}

            <section className="section">

              <div className="two-column">


                {/* ABOUT */}

                <div className="about-card reveal">


                  <div className="section-heading">

                    <h2>
                      ABOUT THE DIVISION
                    </h2>

                    <div
                      className="section-heading-line"
                    />

                  </div>


                  <div className="about-icons">

                    <div className="about-icon">
                      <FaPlus />
                    </div>

                    <div className="about-icon">
                      <FaTruck />
                    </div>

                    <div className="about-icon">
                      <FaHospital />
                    </div>

                    <div className="about-icon">
                      <FaUsers />
                    </div>

                  </div>


                  <p className="about-text">

                    Ashrafia Distributor is the Group's dedicated
                    pharmaceutical distribution division,
                    responsible for the supply of trusted healthcare
                    products across Gujranwala and Sialkot.

                  </p>


                  <p className="about-text">

                    With a strong distribution network and
                    commitment to reliability, we ensure timely
                    delivery of pharmaceutical products to
                    pharmacies, hospitals, and healthcare providers.

                  </p>


                  <p className="about-text">

                    As we expand into new cities, our focus remains
                    on quality, compliance, and service excellence.

                  </p>

                </div>


                {/* SERVICES */}

                <div className="services-card reveal reveal-delay-1">


                  <div className="section-heading">

                    <h2>
                      OUR DISTRIBUTION SERVICES
                    </h2>

                    <div
                      className="section-heading-line"
                    />

                  </div>


                  <div className="services-list">


                    <Service
                      icon={<FaCapsules />}
                      title="Pharmaceuticals & Healthcare Products"
                      description="Wide range of genuine and quality-assured products."
                    />


                    <Service
                      icon={<FaStore />}
                      title="Strong Retail & Pharmacy Network"
                      description="Serving a large network of pharmacies and medical stores."
                    />


                    <Service
                      icon={<FaHospital />}
                      title="Hospital & Institutional Supply"
                      description="Reliable supply to hospitals, clinics and healthcare institutions."
                    />


                    <Service
                      icon={<FaBoxes />}
                      title="Efficient Logistics & Inventory Management"
                      description="Well-managed warehousing and inventory for smooth operations."
                    />


                    <Service
                      icon={<FaChartLine />}
                      title="Expansion into New Cities"
                      description="Continuously expanding our reach to serve more communities."
                    />

                  </div>

                </div>

              </div>

            </section>


            {/* =================================================
                CORE STRENGTHS + COVERAGE
                ================================================= */}

            <section
              className="section section-white"
            >

              <div className="strengths-coverage">


                {/* CORE STRENGTHS */}

                <div className="strength-card reveal">


                  <div className="section-heading">

                    <h2>
                      CORE STRENGTHS
                    </h2>

                    <div
                      className="section-heading-line"
                    />

                  </div>


                  <div className="strength-grid">


                    <Strength
                      icon={<FaHandshake />}
                      text={
                        <>
                          Reliable
                          <br />
                          Supply Chain
                        </>
                      }
                    />


                    <Strength
                      icon={<FaClock />}
                      text={
                        <>
                          Timely
                          <br />
                          Deliveries
                        </>
                      }
                    />


                    <Strength
                      icon={<FaShieldAlt />}
                      text={
                        <>
                          Trusted
                          <br />
                          Partnerships
                        </>
                      }
                    />


                    <Strength
                      icon={<FaFileAlt />}
                      text={
                        <>
                          Regulatory
                          <br />
                          Compliance
                        </>
                      }
                    />


                    <Strength
                      icon={<FaUsers />}
                      text={
                        <>
                          Market
                          <br />
                          Expertise
                        </>
                      }
                    />

                  </div>

                </div>


                {/* COVERAGE */}

                <div
                  className="coverage-card reveal reveal-delay-1"
                >


                  <div className="section-heading">

                    <h2>
                      OUR COVERAGE
                    </h2>

                    <div
                      className="section-heading-line"
                    />

                  </div>


                  <div className="coverage-content">


                    <div className="presence-box">


                      <div className="coverage-label">

                        CURRENT PRESENCE

                      </div>


                      <div className="cities">

                        <div className="city">

                          <FaMapMarkerAlt />

                          Gujranwala

                        </div>


                        <div className="city">

                          <FaMapMarkerAlt />

                          Sialkot

                        </div>

                      </div>


                      <div className="map-box">

                        <div className="map-text">

                          GUJRANWALA &amp; SIALKOT

                          <br />

                          REGION

                        </div>

                      </div>

                    </div>


                    <div className="expansion-box">


                      <div className="coverage-label">

                        EXPANSION

                      </div>


                      <div className="expansion-content">


                        <div className="expansion-icon">

                          <FaMapMarkerAlt />

                        </div>


                        <p className="expansion-text">

                          Expanding to major cities
                          across Pakistan

                        </p>

                      </div>

                    </div>

                  </div>

                </div>

              </div>

            </section>


            {/* =================================================
                WHY CHOOSE US
                ================================================= */}

            <section className="section">

              <div className="bottom-section">


                {/* VAN */}

                <div className="van-wrapper reveal">

                  <img
                    src={distributorVan}
                    alt="Ashrafia Distributor Delivery"
                    className="van-image"
                  />


                  <div className="van-overlay">

                    <p className="van-overlay-text">

                      Reliable Healthcare Distribution

                    </p>

                  </div>

                </div>


                {/* WHY CHOOSE */}

                <div
                  className="why-card reveal reveal-delay-1"
                >


                  <div className="section-heading">

                    <h2>
                      WHY CHOOSE US?
                    </h2>

                    <div
                      className="section-heading-line"
                    />

                  </div>


                  <div className="why-grid">


                    <WhyItem
                      icon={<FaCheck />}
                      text="Strong Local Market Presence"
                    />


                    <WhyItem
                      icon={<FaNetworkWired />}
                      text="Efficient Distribution Channels"
                    />


                    <WhyItem
                      icon={<FaAward />}
                      text="Commitment to Healthcare Standards"
                    />


                    <WhyItem
                      icon={<FaChartLine />}
                      text="Growing Nationwide Network"
                    />

                  </div>

                </div>

              </div>

            </section>


            {/* =================================================
                FOOTER
                ================================================= */}

            <footer className="footer">


              <div className="footer-number">

                11

              </div>


              <div className="footer-company">

                <span className="footer-company-name">

                  ASHRAF GROUP OF INDUSTRIES

                </span>


                <span className="footer-tagline">

                  Building Legacy. Powering the Future.

                </span>

              </div>


              <img
                src={ashrafiaLogo}
                alt="Ashrafia Distributor"
                className="footer-logo"
              />

            </footer>


          </main>

        </div>

      </div>

    </>

  );
}


/* ============================================================
   SERVICE COMPONENT
   ============================================================ */

function Service({
  icon,
  title,
  description
}) {

  return (

    <div className="service-item">

      <div className="service-icon">

        {icon}

      </div>


      <div className="service-content">

        <h3>

          {title}

        </h3>


        <p>

          {description}

        </p>

      </div>

    </div>

  );

}


/* ============================================================
   STRENGTH COMPONENT
   ============================================================ */

function Strength({
  icon,
  text
}) {

  return (

    <div className="strength-item">

      <div className="strength-icon">

        {icon}

      </div>


      <div className="strength-text">

        {text}

      </div>

    </div>

  );

}


/* ============================================================
   WHY CHOOSE COMPONENT
   ============================================================ */

function WhyItem({
  icon,
  text
}) {

  return (

    <div className="why-item">

      <div className="why-icon">

        {icon}

      </div>


      <div className="why-text">

        {text}

      </div>

    </div>

  );

}